import { useEffect, useMemo, useState } from 'react';

import { useGetUsers } from '@vlabs/api/artemy/client';
import type { UserRecord } from '@vlabs/api/artemy/model';

import {
  mergeUsers,
  MIN_USER_SEARCH_LENGTH,
} from '@vlabs/monokit/shared/lib/search-users';

const SEARCH_DEBOUNCE_MS = 200;
const SEARCH_PAGE_SIZE = 50;
const EMPTY_USERS: UserRecord[] = [];

function useSearchUsersByModel(): {
  data?: UserRecord[];
  setValue: (value: string) => void;
  value: string;
} {
  const [value, setValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const query = value.trim();
  const isSearchMode = query.length >= MIN_USER_SEARCH_LENGTH;
  const isDebouncing = isSearchMode && query !== debouncedQuery;
  const canSearch = isSearchMode && !isDebouncing;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(query);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [query]);

  const usersByEmail = useGetUsers(
    { email: debouncedQuery.toLowerCase(), page_size: SEARCH_PAGE_SIZE },
    { query: { enabled: canSearch } },
  );
  const usersByFullName = useGetUsers(
    { full_name: debouncedQuery, page_size: SEARCH_PAGE_SIZE },
    { query: { enabled: canSearch } },
  );

  const isLoading = (
    usersByEmail.isFetching
    || usersByFullName.isFetching
  );
  const users = useMemo(() => mergeUsers(
    usersByEmail.data?.users,
    usersByFullName.data?.users,
  ), [
    usersByEmail.data?.users,
    usersByFullName.data?.users,
  ]);

  // если поиска сейчас нет, то страница должна показывать обычный список пользователей, обычную пагинацию и обычный режим
  if (!isSearchMode) {
    return {
      data: undefined,
      setValue,
      value,
    };
  }

  // если поиск уже активирован, но показывать пока нечего
  if (isDebouncing || isLoading) {
    return {
      data: EMPTY_USERS,
      setValue,
      value,
    };
  }

  // mergeUsers нужен, потому что один и тот же пользователь может прийти из нескольких запросов
  // мы оставляем его один раз по user_id
  return {
    data: users,
    setValue,
    value,
  };
}

export { useSearchUsersByModel };
